import { API_BASE_URL } from '../config/api';

/**
 * Σελίδα «Εκτυπωτές» (14/10/2026): τα endpoints του συμβολαίου — ένα GET με όλη την εικόνα
 * (/printing) και ξεχωριστές εγγραφές ανά κομμάτι. Ο server στέλνει τα COUNT/ids της pg και ως
 * κείμενο: όλα περνούν από το normalizePrinting πριν φτάσουν στην οθόνη.
 */

export type TicketStyle = 'kitchen' | 'bar';
export type PrintMethod = 'text' | 'image' | 'hybrid';
export type PlatformKey = 'efood' | 'wolt' | 'box';
export const PLATFORM_KEYS: PlatformKey[] = ['efood', 'wolt', 'box'];
export const PRINT_METHODS: PrintMethod[] = ['image', 'text', 'hybrid'];

export interface AgentEnv {
  server_url: string;
  store_id: number | string;
  api_key_masked: string | null;
  api_key_set: boolean;
}

export interface PrintingAgent {
  connected: boolean;
  version: string | null;
  connected_since: string | null;
  pc_name: string | null;
  /** Ικανότητες που δηλώνει ο agent ('cash-drawer', 'beep', …)· [] = παλιός ή εκτός σύνδεσης. */
  caps: string[];
  env: AgentEnv;
}

export interface PrintingPrinter {
  id: number;
  name: string;
  windows_printer_name: string | null;
  connection_address: string | null;
  prints_receipts: boolean;
  prints_tickets: boolean;
  ticket_style: TicketStyle;
  paper_width: number;
  print_method: PrintMethod;
  /** 'off' | 'both' (σπάνια 'pin2'/'pin5' από παλιά ρύθμιση — κρατιέται όπως είναι). */
  cash_drawer: string;
  beep_enabled: boolean;
  is_default_receipt: boolean;
  is_active: boolean;
  agent_online: boolean;
  pending_jobs: number;
  jobs_today: number;
}

export interface RoutingRule {
  category_id: number | null;
  printer_id: number;
}

/** Προειδοποίηση του server για τη δρομολόγηση (routes/printing.js)· η σελίδα διαβάζει μόνο τα target_inactive. */
export interface RoutingWarning {
  code: string;
  /** Η στήλη του stores που αφορά (π.χ. 'tickets_takeaway_printer_id'), αλλιώς null. */
  key: string | null;
  printer_id: number | null;
}

export interface PrintingRouting {
  rules: RoutingRule[];
  unrouted_printer_id: number | null;
  delivery_printer_id: number | null;
  /**
   * Στόχοι ανά είδος παραγγελίας (274, απόφαση ιδιοκτήτη 14/10/2026). Ο server στέλνει την τιμή που
   * ΙΣΧΥΕΙ: null και όταν ο αποθηκευμένος εκτυπωτής δεν έχει πια τον ρόλο (→ warnings target_inactive).
   * takeaway/dinein: όλη η παραγγελία σε ΕΝΑ δελτίο εκεί· null = ο πίνακας κατηγοριών.
   */
  takeaway_printer_id: number | null;
  dinein_printer_id: number | null;
  /** Τα παραστατικά των παραγγελιών delivery & πλατφορμών· null = ο κύριος εκτυπωτής αποδείξεων. */
  receipts_delivery_printer_id: number | null;
  /**
   * Ο server ξέρει τους τρεις στόχους (στέλνει τα κλειδιά, έστω null). Παλιός server: false — οι
   * επιλογές δεν φαίνονται, γιατί ό,τι διάλεγε κανείς θα το αγνοούσε σιωπηλά.
   */
  order_targets: boolean;
  /**
   * Περιοχές τραπεζιών (ίδια ρύθμιση με την κάτοψη): ο εκτυπωτής που ΙΣΧΥΕΙ για τις αποδείξεις τους·
   * null = ο κύριος εκτυπωτής αποδείξεων. Παλιός server: [] (και η σελίδα δείχνει «στον κύριο»).
   */
  zones: PrintingZone[];
  warnings: RoutingWarning[];
}

export interface PrintingZone {
  id: number;
  name: string;
  printer_id: number | null;
}

export interface PrintingSwitches {
  takeaway_ticket: boolean;
  dinein_kitchen_ticket: boolean;
  /** null = η πλατφόρμα δεν είναι ενεργή στο κατάστημα. */
  platforms: Record<PlatformKey, boolean | null>;
  /** «Έντονα γράμματα στα δελτία» (276)· null = ο server/η βάση δεν το έχει ακόμη → ο διακόπτης κρύβεται. */
  ticket_bold: boolean | null;
}

export interface ReceiptLook {
  logo_url: string | null;
  /** Κάτω από την επωνυμία (273)· null = τίποτα. */
  header_text: string | null;
  /** «Κείμενο στο τέλος της απόδειξης» (280: ευχαριστώ + παλιό υποσέλιδο σε ένα)· null = τίποτα. */
  thank_you_text: string | null;
}

export type ItemSize = 'normal' | 'bold' | 'large';
export const ITEM_SIZES: ItemSize[] = ['normal', 'bold', 'large'];

/** Επιλογές του δελτίου delivery (280, backend utils/ticketOptions)· οι προεπιλογές = το σημερινό χαρτί. */
export interface DeliveryTicketOptions {
  item_size: ItemSize;
  show_prices: boolean;
  show_area: boolean;
  show_date: boolean;
  show_platform_code: boolean;
  show_store_footer: boolean;
}
export type DeliveryFlag = Exclude<keyof DeliveryTicketOptions, 'item_size'>;
export const DELIVERY_FLAGS: DeliveryFlag[] = ['show_prices', 'show_area', 'show_date', 'show_platform_code', 'show_store_footer'];
export const DELIVERY_DEFAULTS: DeliveryTicketOptions = {
  item_size: 'normal', show_prices: true, show_area: true, show_date: true, show_platform_code: true, show_store_footer: true,
};

export interface TicketOptions {
  delivery: DeliveryTicketOptions;
}

/** Μία εντολή εκτύπωσης όπως τη φτιάχνει ο formatter του server (και την τυπώνει ο agent). */
export interface PrintCommand {
  type: string;
  content?: string;
  left?: string;
  right?: string;
}

export type PreviewKey = 'receipt' | 'takeaway' | 'dinein' | 'delivery';

export interface PreviewSlide {
  key: PreviewKey;
  paper_width: 58 | 80;
  commands: PrintCommand[];
}

export interface PrintingCategory {
  id: number;
  name: string;
  parent_id: number | null;
  is_hidden: boolean;
  /** «__uncategorized__»: τα είδη χωρίς κατηγορία (ο Κατάλογος τα δείχνει χωρίς φάκελο). */
  is_system: boolean;
}

export interface PrintingData {
  agent: PrintingAgent;
  printers: PrintingPrinter[];
  routing: PrintingRouting;
  switches: PrintingSwitches;
  receipt_look: ReceiptLook;
  /** null = ο server/η βάση δεν το έχει ακόμη (πριν από το 280) → οι επιλογές δεν φαίνονται. */
  ticket_options: TicketOptions | null;
  categories: PrintingCategory[];
}

export interface DiscoveredQueue {
  windows_printer_name: string;
  /** id του εκτυπωτή που έχει ήδη αυτή την ουρά, αλλιώς null. */
  already_added: number | null;
}

export type TestStatus = 'completed' | 'failed' | 'pending';

export interface TestPrintResult {
  job_id: number | string | null;
  status: TestStatus;
  error: string | null;
  waited_ms: number | null;
}

export interface NewApiKey {
  api_key: string;
  env_block: string;
}

/** ok + σώμα· `error` = το μήνυμα του server για τον χειριστή (ελληνικά), αν έστειλε. status 0 = δίκτυο. */
export interface ApiResult<T> {
  ok: boolean;
  status: number;
  data: T | null;
  error: string | null;
}

/* ───────────────────────── κανονικοποίηση ───────────────────────── */

const int = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const count = (v: unknown): number => int(v) ?? 0;
const text = (v: unknown): string | null => (typeof v === 'string' ? v : v === null || v === undefined ? null : String(v));
const flag = (v: unknown): boolean => v === true || v === 'true' || v === 1;
const optFlag = (v: unknown): boolean | null => (v === null || v === undefined ? null : flag(v));

export function normalizePrinter(raw: any): PrintingPrinter {
  const method = PRINT_METHODS.includes(raw?.print_method) ? raw.print_method as PrintMethod : 'text';
  return {
    id: Number(raw?.id),
    name: text(raw?.name) || '',
    windows_printer_name: text(raw?.windows_printer_name),
    connection_address: text(raw?.connection_address),
    prints_receipts: flag(raw?.prints_receipts),
    prints_tickets: flag(raw?.prints_tickets),
    ticket_style: raw?.ticket_style === 'bar' ? 'bar' : 'kitchen',
    paper_width: int(raw?.paper_width) === 58 ? 58 : 80,
    print_method: method,
    cash_drawer: text(raw?.cash_drawer) || 'off',
    beep_enabled: flag(raw?.beep_enabled),
    is_default_receipt: flag(raw?.is_default_receipt),
    // Λείπει = ενεργός (όπως το is_active IS NOT FALSE του server).
    is_active: raw?.is_active !== false,
    agent_online: flag(raw?.agent_online),
    pending_jobs: count(raw?.pending_jobs),
    jobs_today: count(raw?.jobs_today),
  };
}

/** Επιλογές δελτίου delivery με τις προεπιλογές — ό,τι δεν αναγνωρίζεται = προεπιλογή (όπως ο server). */
export function normalizeDeliveryOptions(raw: any): DeliveryTicketOptions {
  const out: DeliveryTicketOptions = { ...DELIVERY_DEFAULTS };
  if (ITEM_SIZES.includes(raw?.item_size)) out.item_size = raw.item_size;
  DELIVERY_FLAGS.forEach(k => { if (typeof raw?.[k] === 'boolean') out[k] = raw[k]; });
  return out;
}

const normalizeTicketOptions = (raw: any): TicketOptions | null =>
  (raw && typeof raw === 'object' ? { delivery: normalizeDeliveryOptions(raw.delivery) } : null);

/** Τα πεδία του 274 στο routing: αν λείπουν όλα, ο server είναι πριν από αυτά. */
const ORDER_TARGET_FIELDS = ['takeaway_printer_id', 'dinein_printer_id', 'receipts_delivery_printer_id'];

export function normalizePrinting(raw: any): PrintingData {
  const agent = raw?.agent || {};
  const env = agent.env || {};
  const routing = raw?.routing && typeof raw.routing === 'object' ? raw.routing : {};
  const switches = raw?.switches || {};
  const platforms = switches.platforms || {};
  const look = raw?.receipt_look || {};
  return {
    agent: {
      connected: flag(agent.connected),
      version: text(agent.version),
      connected_since: text(agent.connected_since),
      pc_name: text(agent.pc_name),
      caps: Array.isArray(agent.caps) ? agent.caps.map(String) : [],
      env: {
        server_url: text(env.server_url) || '',
        store_id: env.store_id ?? '',
        api_key_masked: text(env.api_key_masked),
        api_key_set: flag(env.api_key_set),
      },
    },
    printers: (Array.isArray(raw?.printers) ? raw.printers : []).map(normalizePrinter),
    routing: {
      rules: (Array.isArray(routing.rules) ? routing.rules : [])
        .map((r: any) => ({ category_id: int(r?.category_id), printer_id: Number(r?.printer_id) }))
        .filter((r: RoutingRule) => Number.isFinite(r.printer_id)),
      unrouted_printer_id: int(routing.unrouted_printer_id),
      delivery_printer_id: int(routing.delivery_printer_id),
      takeaway_printer_id: int(routing.takeaway_printer_id),
      dinein_printer_id: int(routing.dinein_printer_id),
      receipts_delivery_printer_id: int(routing.receipts_delivery_printer_id),
      order_targets: ORDER_TARGET_FIELDS.some(k => Object.prototype.hasOwnProperty.call(routing, k)),
      zones: (Array.isArray(routing.zones) ? routing.zones : [])
        .map((z: any) => ({ id: Number(z?.id), name: text(z?.name) || '', printer_id: int(z?.printer_id) }))
        .filter((z: PrintingZone) => Number.isFinite(z.id)),
      warnings: (Array.isArray(routing.warnings) ? routing.warnings : [])
        .filter((w: any) => w && typeof w.code === 'string')
        .map((w: any) => ({ code: w.code, key: text(w.key), printer_id: int(w.printer_id) })),
    },
    switches: {
      takeaway_ticket: switches.takeaway_ticket !== false,
      dinein_kitchen_ticket: switches.dinein_kitchen_ticket !== false,
      platforms: { efood: optFlag(platforms.efood), wolt: optFlag(platforms.wolt), box: optFlag(platforms.box) },
      ticket_bold: optFlag(switches.ticket_bold),
    },
    receipt_look: {
      logo_url: text(look.logo_url) || null,
      header_text: text(look.header_text),
      thank_you_text: text(look.thank_you_text),
    },
    ticket_options: normalizeTicketOptions(raw?.ticket_options),
    categories: (Array.isArray(raw?.categories) ? raw.categories : []).map((c: any) => ({
      id: Number(c?.id),
      name: text(c?.name) || '',
      parent_id: int(c?.parent_id),
      is_hidden: flag(c?.is_hidden),
      is_system: flag(c?.is_system),
    })).filter((c: PrintingCategory) => Number.isFinite(c.id)),
  };
}

/* ───────────────────────── αιτήματα ───────────────────────── */

const serverMessage = (data: any): string | null => {
  if (!data || typeof data !== 'object') return null;
  const parts = [data.error, data.message].filter(v => typeof v === 'string' && v.trim() !== '');
  return parts.length > 0 ? parts.join(' — ') : null;
};

async function request<T>(method: string, path: string, body?: unknown): Promise<ApiResult<T>> {
  try {
    const token = localStorage.getItem('xentrix_token');
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const data = await res.json().catch(() => null);
    return { ok: res.ok, status: res.status, data: res.ok ? (data as T) : null, error: res.ok ? null : serverMessage(data) };
  } catch {
    return { ok: false, status: 0, data: null, error: null };
  }
}

const base = (storeId: number) => `/api/stores/${storeId}`;

export const printingApi = {
  load: async (storeId: number): Promise<ApiResult<PrintingData>> => {
    const r = await request<any>('GET', `${base(storeId)}/printing`);
    return r.ok ? { ...r, data: normalizePrinting(r.data) } : r;
  },
  createApiKey: (storeId: number) => request<NewApiKey>('POST', `${base(storeId)}/printing/api-key`),
  revokeApiKey: (storeId: number) => request<unknown>('DELETE', `${base(storeId)}/printing/api-key`),
  discover: async (storeId: number): Promise<ApiResult<DiscoveredQueue[]>> => {
    const r = await request<any>('GET', `${base(storeId)}/printing/discover`);
    if (!r.ok) return r;
    const list = Array.isArray(r.data?.printers) ? r.data.printers : [];
    return {
      ...r,
      data: list
        .map((p: any) => ({ windows_printer_name: text(p?.windows_printer_name) || '', already_added: int(p?.already_added) }))
        .filter((p: DiscoveredQueue) => p.windows_printer_name !== ''),
    };
  },
  createPrinter: (storeId: number, body: Record<string, unknown>) => request<any>('POST', `${base(storeId)}/printers`, body),
  updatePrinter: (storeId: number, id: number, body: Record<string, unknown>) => request<any>('PUT', `${base(storeId)}/printers/${id}`, body),
  deletePrinter: (storeId: number, id: number) => request<unknown>('DELETE', `${base(storeId)}/printers/${id}`),
  testPrint: async (storeId: number, id: number): Promise<ApiResult<TestPrintResult>> => {
    const r = await request<any>('POST', `${base(storeId)}/printers/${id}/test`);
    if (!r.ok) return r;
    const status: TestStatus = r.data?.status === 'completed' || r.data?.status === 'failed' ? r.data.status : 'pending';
    return {
      ...r,
      data: { job_id: r.data?.job_id ?? r.data?.jobId ?? null, status, error: text(r.data?.error), waited_ms: int(r.data?.waited_ms) },
    };
  },
  testDrawer: (storeId: number, id: number, body: Record<string, unknown>) => request<unknown>('POST', `${base(storeId)}/printers/${id}/test-drawer`, body),
  testBeep: (storeId: number, id: number, body: Record<string, unknown>) => request<unknown>('POST', `${base(storeId)}/printers/${id}/test-beep`, body),
  saveRouting: (storeId: number, body: Record<string, unknown>) => request<unknown>('PUT', `${base(storeId)}/printing/routing`, body),
  saveSwitches: (storeId: number, body: Record<string, unknown>) => request<unknown>('PUT', `${base(storeId)}/printing/switches`, body),
  saveReceiptLook: (storeId: number, body: Record<string, unknown>) => request<unknown>('PUT', `${base(storeId)}/printing/receipt-look`, body),
  saveTicketOptions: (storeId: number, body: Record<string, unknown>) => request<unknown>('PUT', `${base(storeId)}/printing/ticket-options`, body),
  /** Κάθε χαρτί από τον formatter της εκτύπωσης, με ό,τι δείχνει τώρα η σελίδα (POST /printing/preview). */
  preview: async (storeId: number, body: Record<string, unknown>): Promise<ApiResult<PreviewSlide[]>> => {
    const r = await request<any>('POST', `${base(storeId)}/printing/preview`, body);
    if (!r.ok) return r;
    const slides = (Array.isArray(r.data?.slides) ? r.data.slides : [])
      .filter((s: any) => s && typeof s.key === 'string' && Array.isArray(s.commands))
      .map((s: any) => ({ key: s.key as PreviewKey, paper_width: Number(s.paper_width) === 58 ? 58 : 80, commands: s.commands as PrintCommand[] }));
    return { ...r, data: slides };
  },
};
